import React, { useCallback, useMemo } from 'react';

import { Box, Flex, Image } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { openCreateInitiativePanelAction } from 'actions/createInitiativesPanelActions';
import { openPanel } from 'components/RabbitInitiativeCreationPanel/InitiativeCreationPanelSlice';
import { closeSecondaryPanel } from 'components/RabbitInitiativeSidePanel/initiativeSecondaryPanelSlice';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { SimpleComponent } from 'screens/Initiatives/InitiativeAudit/AGGridComponents';
import { useProfilePage } from 'screens/Profile/hooks/dataHooks';
import { useInitiativeTableDataQuery } from 'screens/home/summary/components/Cards/Initiatives/hooks';
import { useOneOnOneInstanceDetails } from 'screens/oneOnOneRabbit/InstancePage/hooks/dataHooks';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';

import { InitiativesIconDualTone, Plus } from 'assets/assetsV1/iconComponents';
import NoInitiativesImage from 'assets/assetsV1/illustrations/noInitiatives.svg';

import { sendOpenedKRPanelEvent } from 'constants/mixpanelHelper';

import { showTaskDetails } from 'services/taskDetails';

import { AgGridWrapper } from 'uiToolkitV1/AgGridWrapper';
import { Button } from 'uiToolkitV1/Button';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { SidebarCardTemplate } from '../common/components/SidebarCardTemplate';

const TruncatedTextMedium = styled(TextBodyMedium)`
  display: -webkit-box;
  max-width: 200px;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// copy pasta from profile summary card
const InitiativesAgGrid = ({ tableData, pageType = 'Home Page' }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const showDetails = (e, taskId, type) => {
    e?.stopPropagation();

    dispatch(
      // @ts-ignore
      showTaskDetails({
        taskIds: [taskId],
        onDelete: () => {},
        onUpdate: () => {},
        employee: {},
        type,
        history,
      })
    );

    sendOpenedKRPanelEvent({
      eventProperties: {
        source: pageType,
      },
    });
  };

  const handleViewDetails = ({ event, data, column }) => {
    if (column.colId !== '0') {
      const { uuid, type } = data;
      showDetails(event, uuid, type);
    }
  };

  const columnDefs = useMemo(() => {
    const columns = [
      {
        headerName: 'Initiatives',
        field: 'title',
        valueGetter: (data) => ({ title: data.data?.title, isCompleted: data.data?.completionTS !== null }),
        flex: 1,

        cellRenderer: (params) => {
          const { title, isCompleted } = params?.value ?? {};
          return (
            <Flex flexDir="column" paddingY="8px">
              <Flex height="100%" alignItems="center">
                <TruncatedTextMedium
                  style={isCompleted ? { textDecoration: 'line-through' } : { textDecoration: 'none' }}
                >
                  {title}
                </TruncatedTextMedium>
              </Flex>

              <Box mt="12px">
                <SimpleComponent {...params} />
              </Box>
            </Flex>
          );
        },
      },
    ];
    return columns;
  }, []);

  const getRowStyle = () => ({ cursor: 'pointer' });

  const getRowId = useCallback(({ data }) => data?.uuid, []);

  const gridStatePersistenceKey = null;

  return (
    <Box className="ag-theme-alpine" style={{ width: '100%' }}>
      <AgGridWrapper
        gridStatePersistenceKey={gridStatePersistenceKey}
        useColumnStatePersistence={false}
        useFilterModelPersistence={false}
        defaultColDef={{
          autoHeight: true,
          wrapText: true,
        }}
        // @ts-ignore
        columnDefs={columnDefs}
        rowData={tableData}
        domLayout="autoHeight"
        animateRows={false}
        //   @ts-ignore
        onCellClicked={handleViewDetails}
        getRowStyle={getRowStyle}
        rowHeight={80}
        getRowId={getRowId}
        headerHeight={0}
      />
    </Box>
  );
};

const InitiativesSection = () => {
  const { masterId, instanceId } = useOneOnOnePageParams();

  const { receiverEmployee } = useOneOnOneInstanceDetails({
    masterId,
    instanceId,
  });

  const { id: employeeId, displayName } = receiverEmployee;

  const { data: employee } = useProfilePage({ employeeId });

  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);
  const dispatch = useDispatch();

  const isLoggedInEmployeeTheReceiver = loggedInEmployeeId === employeeId;

  const { data, isLoading } = useInitiativeTableDataQuery({
    attributeFilter: {
      filterConditions: [
        {
          type: 'NAME',
          operatorType: 'IN',
          data: [employeeId],
        },
        {
          type: 'TYPE',
          operatorType: 'IN',
          data: ['TASK'],
        },
      ],
    },
    employeeId,
    pageType: 'PROFILE_PAGE',
  });

  const addInitiativeHandler = () => {
    const updatedToIds = [
      {
        uuid: employee?.id,
        ownerType: 'OWNER',
        metadata: { displayName: employee?.displayName },
        profile: { profilePhoto: employee?.profilePhotoSrc },
      },
    ];

    // @ts-ignore
    dispatch(closeSecondaryPanel()); /// // WILL CLOSE SECONDARY DRAWER ON INITIAL OPENING ON ADD NEW INITIATIVE PANEL

    dispatch(
      openPanel({
        toIds: updatedToIds,
      })
    );

    dispatch(
      // @ts-ignore
      openCreateInitiativePanelAction({
        // onClose: closeCreateInitiativeSidePanel,
        // onCancel: closeCreateInitiativeSidePanel,
        // team: cohortDetails,
        // onSave: handleNewInitiativeSave,
        selectedTimeBoundPeriod: {},
        ownerEmployee: {
          email: employee?.email,
          uuid: employee?.id,
          displayName: employee?.displayName,
          profilePhoto: employee?.profilePhotoSrc,
          designation: employee?.designation,
          employeeFunction: employee?.employeeFunction,
          ownerType: 'OWNER',
        },
        subObjective: null,
      })
    );
  };

  if (isLoading) {
    return <SkeletonLoader />;
  }
  if (!data?.computedData?.length) {
    return (
      <SidebarCardTemplate label="Initiatives" Icon={InitiativesIconDualTone}>
        <Flex mt="24px" flexDir="column" alignItems="center" justifyContent="center">
          <Image width="172px" src={NoInitiativesImage as unknown as string} />
          <Flex w="245px" textAlign="center" mt="22px" flexDir="column" p="12px">
            <TextBodyMedium bold>
              {isLoggedInEmployeeTheReceiver
                ? "You haven't created an Initiative"
                : `${displayName} doesn't have any initiatives`}
            </TextBodyMedium>
            <TextBodyMedium color="text.g2">
              {isLoggedInEmployeeTheReceiver
                ? 'The best way to have a garden full of roses is to plant them! Take initiative and get started today!'
                : 'Help your colleague achieve their goals by adding initiatives for them'}
            </TextBodyMedium>
          </Flex>
        </Flex>
        <Button onClick={addInitiativeHandler} StartIcon={Plus} alignSelf="center" mt="22px">
          Add New Initiative
        </Button>
      </SidebarCardTemplate>
    );
  }
  return (
    <SidebarCardTemplate
      label="Initiatives"
      Icon={InitiativesIconDualTone}
      AddButton={() => <Button StartIcon={Plus} variant="outlined" onClick={addInitiativeHandler} />}
    >
      <InitiativesAgGrid tableData={data?.computedData} pageType="Module Data in 1:1s" />
    </SidebarCardTemplate>
  );
};

export { InitiativesSection };
